import { useMemo, useRef, useState } from "react";
import { ScrollView, Text, type TextInput } from "react-native";
import { useTranslation } from "react-i18next";
import {
  buildModelDraft,
  buildPayload,
  coerceLocale,
  modelRowAt,
  modelRowExercise,
  pickLocalizedText,
  SESSION_MINUTES,
  type EffortScale,
  type Exercise,
  type ModelLevel,
  type NewProgram,
  type ProgramModel,
  type SessionMinutes,
} from "@heraion/shared";

import { difficultyLabel } from "../Library/difficultyLabel";
import { Button } from "../primitives/Button";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { FieldLabel } from "../primitives/FieldLabel";
import { TextField } from "../primitives/TextField";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import type { UiTheme } from "../theme";
import { ModelWizardDay, type WizardRow } from "./ModelWizardDay";
import { makeSetupStyles } from "./setupStyles";

/** The program name cap: Program.name's schema max (120). The editor's own
 *  field sets none, so a longer name would only fail at save. */
const NAME_MAX = 120;

/** The default time per session: the model's fullest (every exercise). */
const DEFAULT_MINUTES: SessionMinutes = SESSION_MINUTES[SESSION_MINUTES.length - 1]!;

/** «9-10»: how many days a round lasts when `days` sessions run `perWeek`
 *  times a week. */
const roundDays = (days: number, perWeek: number): string => {
  const d = (days * 7) / perWeek;
  return Math.floor(d) === Math.ceil(d) ? String(d) : `${Math.floor(d)}-${Math.ceil(d)}`;
};

// A program model's wizard (#351, brief §15 row 33, flow 8): the name, the
// level, the time per session, the sessions a week and the length, each only
// among the model's own values; then each day's rows, whose exercise can be
// swapped for the row's alternatives or taken out. Everything has a default,
// so after the name «Create» works in one tap. «Create» builds the draft
// (buildModelDraft) and hands its payload to the composer as the creation
// seed. «Create» is never grey: tapped too early it says what is missing,
// and takes the focus to the name when that is it.
export function ModelWizard({
  theme,
  model,
  catalog,
  effortScale,
  onCreate,
  onBack,
}: {
  theme: UiTheme;
  model: ProgramModel;
  catalog: ReadonlyArray<Exercise> | undefined;
  effortScale: EffortScale;
  onCreate: (seed: NewProgram) => void;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const resolveName = useExerciseNameResolver(catalog);
  const rounds = model.roundPattern.length;
  const [name, setName] = useState(() => pickLocalizedText(model.name, lang));
  const [level, setLevel] = useState<ModelLevel>(model.levels[0]!);
  const [minutes, setMinutes] = useState<SessionMinutes>(DEFAULT_MINUTES);
  const [perWeek, setPerWeek] = useState(model.sessionsPerWeek[0]!);
  const [blocks, setBlocks] = useState(model.blocks[0]!);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const nameRef = useRef<TextInput>(null);
  const scrollRef = useRef<ScrollView>(null);
  const dayY = useRef<Record<string, number>>({});

  const days = useMemo(
    () =>
      model.days.map((day) => ({
        id: day.id,
        name: pickLocalizedText(day.name, lang),
        rows: day.rows.flatMap((row): WizardRow[] => {
          const entry = modelRowAt(row, level, minutes);
          return entry
            ? [{ id: row.id, exercises: entry.exercises, exercise: modelRowExercise(entry, picks[row.id]) }]
            : [];
        }),
      })),
    [model, lang, level, minutes, picks],
  );
  const toggleRemove = (id: string) => {
    // A row taken out has no alternatives to show.
    setOpen(null);
    setRemoved((old) => {
      const next = new Set(old);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };
  const empty = days.some((d) => d.rows.every((r) => removed.has(r.id)));
  const noName = name.trim().length === 0;
  const why = !tried
    ? null
    : noName
      ? t("editorV3.tetrad.needName", { defaultValue: "Give the program a name first." })
      : empty
        ? t("editorV3.tetrad.needDay", { defaultValue: "Each day needs at least one exercise: put one back." })
        : null;

  const create = () => {
    if (noName || empty) {
      setTried(true);
      if (noName) nameRef.current?.focus();
      else {
        // The empty day says so in place: take it into view.
        const bare = days.find((d) => d.rows.every((x) => removed.has(x.id)));
        const y = bare ? dayY.current[bare.id] : undefined;
        if (y !== undefined) scrollRef.current?.scrollTo({ y, animated: true });
      }
      return;
    }
    const kept: ProgramModel = {
      ...model,
      days: model.days.map((d) => ({ ...d, rows: d.rows.filter((r) => !removed.has(r.id)) })),
    };
    const draft = buildModelDraft(kept, {
      name: name.trim(),
      level,
      minutes,
      sessionsPerWeek: perWeek,
      blocks,
      exercisePicks: picks,
      effortScale,
      editLocale: lang,
      exerciseName: (slug) => resolveName(slug),
    });
    // A model has no kg (§15 row 29): the unit is never read.
    const built = buildPayload(draft, "kg");
    if (built.ok) onCreate(built.payload);
  };

  const minutesHint =
    minutes === 45
      ? t("editorV3.model.minutes45", { defaultValue: "No optional exercises, and one set fewer on the accessories." })
      : minutes === 60
        ? t("editorV3.model.minutes60", { defaultValue: "No optional exercises." })
        : t("editorV3.model.minutes75", { defaultValue: "Every exercise of the model." });

  return (
    <ScrollView ref={scrollRef} contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Text style={styles.title} accessibilityRole="header">
        {pickLocalizedText(model.name, lang)}
      </Text>
      <Text style={styles.hint}>{pickLocalizedText(model.description, lang)}</Text>
      <FieldLabel theme={theme}>{t("program.name", { defaultValue: "Name" })}</FieldLabel>
      <TextField
        ref={nameRef}
        theme={theme}
        touchFloor
        value={name}
        onChangeText={setName}
        maxLength={NAME_MAX}
        selectTextOnFocus
        accessibilityLabel={t("program.name", { defaultValue: "Name" })}
      />
      <ChipChoiceRow
        theme={theme}
        choices={model.levels}
        selected={level}
        onSelect={setLevel}
        label={(l) => difficultyLabel(l, t)}
        chipA11yLabel={(l) => difficultyLabel(l, t)}
        groupA11yLabel={t("editorV3.model.level", { defaultValue: "Level" })}
        title={t("editorV3.model.level", { defaultValue: "Level" })}
      />
      <ChipChoiceRow
        theme={theme}
        choices={SESSION_MINUTES}
        selected={minutes}
        onSelect={setMinutes}
        label={String}
        chipA11yLabel={(n) => t("editorV3.model.minutesA11y", { count: n, defaultValue: "{{count}} minutes" })}
        groupA11yLabel={t("editorV3.model.minutes", { defaultValue: "Minutes per session" })}
        title={t("editorV3.model.minutes", { defaultValue: "Minutes per session" })}
      />
      <Text style={styles.hint}>{minutesHint}</Text>
      <ChipChoiceRow
        theme={theme}
        choices={model.sessionsPerWeek}
        selected={perWeek}
        onSelect={setPerWeek}
        label={String}
        chipA11yLabel={(n) => t("editorV3.tetrad.perWeekA11y", { count: n, defaultValue: "{{count}} sessions a week" })}
        groupA11yLabel={t("editorV3.tetrad.perWeek", { defaultValue: "Sessions a week" })}
        title={t("editorV3.tetrad.perWeek", { defaultValue: "Sessions a week" })}
      />
      {perWeek !== model.days.length ? (
        <Text style={styles.hint}>
          {t("editorV3.model.rotateHint", {
            count: model.days.length,
            length: roundDays(model.days.length, perWeek),
            defaultValue: "The {{count}} days take turns: a round lasts {{length}} days.",
          })}
        </Text>
      ) : null}
      <ChipChoiceRow
        theme={theme}
        choices={model.blocks}
        selected={blocks}
        onSelect={setBlocks}
        label={(b) => String(b * rounds)}
        chipA11yLabel={(b) => t("editorV3.tetrad.roundsA11y", { count: b * rounds, defaultValue: "{{count}} rounds" })}
        groupA11yLabel={t("editorV3.tetrad.rounds", { defaultValue: "Rounds" })}
        title={t("editorV3.tetrad.rounds", { defaultValue: "Rounds" })}
      />
      {blocks > 1 ? (
        <Text style={styles.hint}>
          {t("editorV3.model.blocksHint", {
            count: blocks,
            defaultValue: "The model runs {{count}} times: each time starts again a step higher.",
          })}
        </Text>
      ) : null}
      {days.map((day) => (
        <ModelWizardDay
          key={day.id}
          theme={theme}
          name={day.name}
          rows={day.rows}
          removed={removed}
          open={open}
          nameOf={(slug) => resolveName(slug)}
          onLayoutY={(y) => {
            dayY.current[day.id] = y;
          }}
          onToggleRemove={toggleRemove}
          onToggleOpen={(id) => setOpen((o) => (o === id ? null : id))}
          onPick={(id, slug) => {
            setPicks((p) => ({ ...p, [id]: slug }));
            setOpen(null);
          }}
        />
      ))}
      <Button theme={theme} label={t("program.create", { defaultValue: "Create" })} onPress={create} full />
      {why ? (
        <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
          {why}
        </Text>
      ) : null}
      <Button theme={theme} variant="ghost" label={t("editorV3.tetrad.back", { defaultValue: "Back" })} onPress={onBack} full />
    </ScrollView>
  );
}
